import { useAuth } from "@/context/authContext";
import { api } from "@/utils/api";
import { useState, type Dispatch, type SetStateAction } from "react";
import { toast } from "sonner";

export type DashboardApiBase = "admin" | "advisor";

const studentsUrl = (apiBase: DashboardApiBase) =>
  apiBase === "advisor"
    ? "/dashboard/teacher/advisees"
    : "/dashboard/admin/students";

const sessionsUrl = (apiBase: DashboardApiBase) =>
  apiBase === "advisor"
    ? "/dashboard/teacher/advisees/manage/sessions"
    : "/dashboard/admin/manage/sessions";

const studentSessionUrl = (
  apiBase: DashboardApiBase,
  studentId: string,
  sessionId: string,
) =>
  apiBase === "advisor"
    ? `/dashboard/teacher/advisees/manage/${studentId}/session/${sessionId}`
    : `/dashboard/admin/manage/${studentId}/session/${sessionId}`;

export const useLoadDashboard = (
  { apiBase }: { apiBase?: DashboardApiBase } = {},
) => {
  const base: DashboardApiBase = apiBase ?? "admin";
  const { dispatch } = useAuth();
  const [loading, setLoading] = useState<{
    missingEnrollments: boolean;
    attendance: boolean;
    students: boolean;
    teachers: boolean;
    sessionsData: boolean;
    studentSession: boolean;
  }>({
    missingEnrollments: false,
    attendance: false,
    students: false,
    teachers: false,
    sessionsData: false,
    studentSession: false,
  });

  const loadMissingEnrollment = async (
    setMissingEnrollment: (data: any) => void,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, missingEnrollments: true }));
      const response = await api(
        "/dashboard/admin/missing-enrollment",
        {
          method: "GET",
        },
        dispatch,
      );
      setMissingEnrollment(response.data);
    } catch {
      toast.error("An error occurred, please refresh page");
    } finally {
      setLoading((prev) => ({ ...prev, missingEnrollments: false }));
    }
  };

  const loadAttendance = async (setAttendance: (data: any) => void) => {
    try {
      setLoading((prev) => ({ ...prev, attendance: true }));
      const response = await api(
        "/dashboard/admin/attendance",
        {
          method: "GET",
        },
        dispatch,
      );
      setAttendance(response.data);
    } catch {
      toast.error("An error occurred, please refresh page");
    } finally {
      setLoading((prev) => ({ ...prev, attendance: false }));
    }
  };

  const loadStudents = async (setStudents: (data: any) => void) => {
    try {
      setLoading((prev) => ({ ...prev, students: true }));
      const response = await api(
        studentsUrl(base),
        { method: "GET" },
        dispatch,
      );
      setStudents(response.data);
    } catch {
      toast.error("An error occurred, please refresh page");
    } finally {
      setLoading((prev) => ({ ...prev, students: false }));
    }
  };

  const loadTeachers = async (
    setTeachers: Dispatch<SetStateAction<any[] | null>>,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, teachers: true }));
      const response = await api(
        "/auth/users?shape=teachers",
        { method: "GET" },
        dispatch,
      );
      setTeachers(response.teachers);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, teachers: false }));
    }
  };

  const loadSessionsData = async (setSessionsData: (data: any) => void) => {
    try {
      setLoading((prev) => ({ ...prev, sessionsData: true }));
      const response = await api(
        sessionsUrl(base),
        { method: "GET" },
        dispatch,
      );
      setSessionsData({
        rotations: response.rotations,
        sessions: response.sessions,
      });
    } catch {
      toast.error("An error occurred, please refresh page");
    } finally {
      setLoading((prev) => ({ ...prev, sessionsData: false }));
    }
  };

  const loadStudentSession = async (
    studentId: string,
    sessionId: string,
    setStudentSession: (data: any) => void,
    setUpdated: Dispatch<
      SetStateAction<{
        enrollmentExploration: string;
        preferenceChoices: [string, string, string];
        attendance: { classId: string; present: boolean; reason?: string }[];
      } | null>
    >,
  ) => {
    try {
      setLoading((prev) => ({ ...prev, studentSession: false }));
      const response = await api(
        studentSessionUrl(base, studentId, sessionId),
        { method: "GET" },
        dispatch,
      );
      setStudentSession({
        enrollment: response.enrollment,
        preferences: response.preferences,
        attendance: response.attendance,
        explorations: response.explorations,
        classes: response.classes,
      });
      setUpdated({
        enrollmentExploration: response.enrollment?.exploration ?? "",
        preferenceChoices: response.preferences?.choices ?? [],
        attendance: response.attendance.map((a: any) => {
          return {
            classId: a.class,
            present: a.present,
            reason: a.reason ?? "",
          };
        }),
      });
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading((prev) => ({ ...prev, studentSession: false }));
    }
  };

  return {
    loadMissingEnrollment,
    loadAttendance,
    loadStudents,
    loadTeachers,
    loadSessionsData,
    loadStudentSession,
    loading,
  };
};
